import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { couponKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import NavTabs from '@dashboard/components/NavTabs';
import DiscountsCouponsDetail from './components/DiscountsCouponsDetail';
import CardHeader from '@/dashboard/components/CardHeader';
import { OrderCardMenu, OrderPageHeader, OrderPageSkeleton } from '@dashboard/components/OrderPage';
import { getCouponDiscountLabel } from '@dashboard/components/DiscountsCoupons/couponLabels';
import { toast } from '@/dashboard/context/ToastContext';
import Button, { getButtonClass } from '@/dashboard/components/Button';
import Tooltip from '@dashboard/components/Tooltip';
import { cn } from '@dashboard/lib/utils';
import { useAdminGhosting } from '@/dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { confirm } from '@/dashboard/lib/confirm';
import { useHistory } from 'react-router';
import { isAfter } from 'date-fns';
import CouponStatusBadge from '@/dashboard/components/DiscountsCoupons/CouponStatusBadge';
import DiscountsCouponsPaymentsRedemptions from './components/DiscountsCouponsPaymentsRedemptions';
import DiscountsCouponsBookingsRedemptions from './components/DiscountsCouponsBookingsRedemptions';
import DiscountsCouponsSubscriptionsRedemptions from './components/DiscountsCouponsSubscriptionsRedemptions';
import CouponEditDialogLabs from './components/CouponEditDialogLabs';

// https://github.com/checkout-page/checkoutpage-api/commit/6920e1c2dfc2f0c52c92a094ceb45864be460946
const handleRedemptionLimitsInCheckoutPageMergeData = '2025-10-04T06:09:43.000Z';

// Coupons created on or before this cutoff predate syncing redemption limits to
// Stripe and can no longer be edited. Render the date in UTC so every seller
// sees the same cutoff regardless of their timezone.
const couponEditCutoffLabel = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
}).format(new Date(handleRedemptionLimitsInCheckoutPageMergeData));

const couponNotEditableMessage = `Coupons created on or before ${couponEditCutoffLabel} can't be edited.`;

const getTabs = ({ coupon }) => [
  {
    name: 'Details',
    href: `/coupons/${coupon._id}`,
    render: props => <DiscountsCouponsDetail {...props} coupon={coupon} />,
  },
  {
    name: `One-time (${coupon.paymentsRedemptionCount})`,
    href: `/coupons/${coupon._id}/one-time-redemptions`,
    render: props => <DiscountsCouponsPaymentsRedemptions {...props} coupon={coupon} />,
  },
  {
    name: `Subscriptions (${coupon.subscriptionsRedemptionCount})`,
    href: `/coupons/${coupon._id}/subscriptions-redemptions`,
    render: props => <DiscountsCouponsSubscriptionsRedemptions {...props} coupon={coupon} />,
  },
  {
    name: `Bookings (${coupon.eventBookingsRedemptionCount})`,
    href: `/coupons/${coupon._id}/event-booking-redemptions`,
    render: props => <DiscountsCouponsBookingsRedemptions {...props} coupon={coupon} />,
  },
];

const DiscountsCouponsDetailOverviewLabs = () => {
  const { token, sellerId } = useAuth();
  const {
    params: { id: couponId },
  } = useRouteMatch();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const history = useHistory();

  const {
    data: coupon,
    isError,
    isLoading,
  } = useQuery({
    queryKey: couponKey(couponId),
    queryFn: () => fetcher(`/sellers/${sellerId}/coupons/${couponId}`, token),
    enabled: !!couponId,
  });

  useEffect(() => {
    if (coupon?.seller) {
      setAdminGhostingSellerId(coupon.seller);
    }
  }, [coupon?.seller, setAdminGhostingSellerId]);

  async function deleteCoupon() {
    try {
      const result = await confirm('Delete this coupon?', {
        confirm: 'Delete',
      });

      if (!result) return;

      await fetcher(`/sellers/${sellerId}/coupons/${couponId}`, token, {
        method: 'DELETE',
      });

      queryClient.invalidateQueries({ queryKey: couponKey(couponId) });
      toast({
        title: 'Coupon deleted',
        status: 'success',
      });
      history.push('/coupons');
    } catch (err) {
      // await confirm is a promise and rejects the cancellation.
      if (err === false) return;

      toast({
        title: 'Unable to delete coupon',
        description: 'Please try again or contact support.',
        status: 'error',
      });
    }
  }

  if (isLoading) {
    return <OrderPageSkeleton variant="coupon" tabs={4} actions={false} />;
  }

  if (!coupon || isError) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ coupon });

  return (
    <>
      <Card variant="root" contentColumn>
        <CardHeader variant="root">
          <OrderPageHeader
            title={`Coupon ${coupon.code}`}
            badges={<CouponStatusBadge coupon={coupon} />}
            date={[coupon.label, getCouponDiscountLabel(coupon)].filter(Boolean).join(' · ')}
          >
            {!coupon.deleted && (
              <div className="flex items-center gap-2">
                <OrderCardMenu
                  label="More coupon actions"
                  items={[{ label: 'Delete coupon', onSelect: deleteCoupon }]}
                />
                {isAfter(coupon.createdAt, handleRedemptionLimitsInCheckoutPageMergeData) ? (
                  <Button appearance="primary" onClick={() => setEditDialogOpen(true)}>
                    Edit coupon
                  </Button>
                ) : (
                  <Tooltip label={couponNotEditableMessage}>
                    <span
                      className={cn(getButtonClass('primary'), 'cursor-not-allowed opacity-50')}
                    >
                      Edit coupon
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </OrderPageHeader>

          <NavTabs aria-label="Coupon detail">
            {tabs.map(tab => (
              <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
                {tab.name}
              </NavTabs.Item>
            ))}
          </NavTabs>
        </CardHeader>

        <CardContent variant="compact">
          <Switch>
            {tabs.map(tab => (
              <Route key={tab.href} path={tab.href} render={tab.render} exact />
            ))}
          </Switch>
        </CardContent>
      </Card>

      <CouponEditDialogLabs
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        coupon={coupon}
      />
    </>
  );
};

export default DiscountsCouponsDetailOverviewLabs;
